import { inject, Service } from "@angular/core";

import { AdaaHelper } from "../../core/utils";
import { PageMode } from "../constants/enums";
import {
  CommonKpiModelType,
  MainResponseModel,
  ProjectDs3Model,
  ProjectsDashboardFilter,
  ProjectsDashboardSummary,
} from "../models";
import { MainService } from ".";

@Service()
export class ProjectApiService {
  private readonly _mainService = inject(MainService);

  public getById(id: number, year?: number) {
    const qs = new URLSearchParams();
    if (year) qs.set("year", `${year}`);

    return this._mainService.sendRequest<MainResponseModel<ProjectDs3Model>>({
      method: "GET",
      url: `project/getById/${id}?${qs.toString()}`,
    });
  }

  public saveProject(data: unknown, pageMode: PageMode, isFinish = false) {
    const url = `project/${pageMode === PageMode.create ? "create" : "update"}?isFinish=${isFinish}`;

    return this._mainService.sendRequest<MainResponseModel>({
      method: pageMode === PageMode.create ? "POST" : "PUT",
      url: url,
      data,
    });
  }

  public deleteById(id: number) {
    return this._mainService.sendRequest<MainResponseModel<unknown>>({
      method: "DELETE",
      url: `project/deleteById/${id}`,
    });
  }

  public getAll(type?: number) {
    return this._mainService.sendRequest<MainResponseModel<ProjectDs3Model[]>>({
      method: "POST",
      url: type ? `project/getAll?type=${type}` : `project/getAll`,
      data: {},
    });
  }

  public getAllDS3() {
    return this._mainService.sendRequest<MainResponseModel<ProjectDs3Model[]>>({
      method: "POST",
      url: "project/getAllByEntityIdWithPagination?offset=0",
      data: {},
    });
  }

  public getOpms(projectId: number, periodId: number) {
    const qs = new URLSearchParams();
    if (projectId) qs.set("projectId", `${projectId}`);
    if (periodId) qs.set("periodId", `${periodId}`);

    return this._mainService.sendRequest<MainResponseModel<CommonKpiModelType[]>>({
      method: "GET",
      url: `project/opms?${qs.toString()}`,
    });
  }

  public dashboardSummary(filter: ProjectsDashboardFilter) {
    const qs = new URLSearchParams();
    qs.set("entityId", `${filter.entityId ? filter.entityId : AdaaHelper.entity?.id}`);

    if (!filter.showAll) {
      if (filter.periodId) qs.set("periodId", `${filter.periodId}`);
      if (filter.year) qs.set("year", `${filter.year}`);
    }
    if (filter.projectType) qs.set("projectType", `${filter.projectType}`);
    if (filter.projectStatus) qs.set("projectStatus", `${filter.projectStatus}`);

    return this._mainService.sendRequest<MainResponseModel<ProjectsDashboardSummary>>({
      method: "GET",
      url: `project/valuesSummary?${qs.toString()}`,
    });
  }

  public dashboardProjects(filter: ProjectsDashboardFilter) {
    const qs = new URLSearchParams();
    qs.set("entityId", `${filter.entityId ? filter.entityId : AdaaHelper.entity?.id}`);

    if (!filter.showAll) {
      if (filter.periodId) qs.set("periodId", `${filter.periodId}`);
      if (filter.year) qs.set("year", `${filter.year}`);
    }
    if (filter.projectType) qs.set("projectType", `${filter.projectType}`);
    if (filter.projectStatus) qs.set("projectStatus", `${filter.projectStatus}`);
    if (AdaaHelper.isDefined(filter.limit)) qs.set("limit", `${filter.limit}`);
    if (AdaaHelper.isDefined(filter.offset)) qs.set("offset", `${filter.offset}`);

    return this._mainService.sendRequest<MainResponseModel<ProjectDs3Model[]>>({
      method: "GET",
      url: `project/values?${qs.toString()}`,
    });
  }

  public changeType(projectId: number) {
    const qs = new URLSearchParams();
    if (projectId) qs.set("projectId", `${projectId}`);

    return this._mainService.sendRequest<MainResponseModel>({
      method: "POST",
      url: `project/changeType?${qs.toString()}`,
      data: {},
    });
  }
}
